import React, { useState } from "react";
import { Select } from "../design/select";
import { Input } from "../design/input";

const countries = [
  { value: "+1", label: "+1 (US)", maxDigits: 10 },
  { value: "+44", label: "+44 (UK)", maxDigits: 10 },
];

interface Country {
  value: string;
  label: string;
  maxDigits: number;
}

interface PhoneNumberInputProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

const formatPhoneNumber = (phoneNumber: string) => {
  const cleaned = phoneNumber.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{1,3})(\d{1,3})?(\d{1,4})?$/);
  if (match) {
    const part1 = match[1] ? `(${match[1]})` : "";
    const part2 = match[2] ? ` ${match[2]}` : "";
    const part3 = match[3] ? `-${match[3]}` : "";
    return `${part1}${part2}${part3}`;
  }
  return phoneNumber;
};

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  placeholder,
  value,
  onChange,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [phoneNumber, setPhoneNumber] = useState<string>(value || "");

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = countries.find(
      (country) => country.value === e.target.value,
    );
    if (selectedValue) {
      setSelectedCountry(selectedValue);
      updatePhoneNumber(selectedValue.value, phoneNumber);
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawPhoneNumber = e.target.value.replace(/\D/g, "");
    const maxAllowedLength = selectedCountry.maxDigits;
    const trimmedPhoneNumber = rawPhoneNumber.slice(0, maxAllowedLength);
    const formattedPhoneNumber = formatPhoneNumber(trimmedPhoneNumber);
    setPhoneNumber(formattedPhoneNumber);
    updatePhoneNumber(selectedCountry.value, formattedPhoneNumber);
  };

  const updatePhoneNumber = (
    countryCode: string,
    formattedPhoneNumber: string,
  ) => {
    const combinedPhoneNumber = `${countryCode} ${formattedPhoneNumber}`;
    onChange(combinedPhoneNumber);
  };

  return (
    <div className="mt-3 space-y-4">
      <div className="flex items-center space-x-2">
        {/* Country Dropdown */}
        <div className="flex-shrink-0">
          <Select value={selectedCountry.value} onChange={handleCountryChange}>
            {countries.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Phone Number Input */}
        <div className="flex-grow">
          <Input
            type="tel"
            placeholder={placeholder}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
          />
        </div>
      </div>
    </div>
  );
};

export default PhoneNumberInput;
